import { AbstractControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { ipV4Pattern, ipV6Pattern } from '../regex-patterns';

export class CustomValidatorsFactory {
  public static createPhoneValidator(): ValidatorFn {
    // accept exactly 12 digits: 2-digit country code + 10-digit phone number
    return (control: AbstractControl) => {
      if (!control.value) return null; // allow empty
      const digitsOnly = (control.value || '').replace(/\D/g, ''); // extract only digits
      const isValid = digitsOnly.length === 12; // exactly 12 digits
      return isValid ? null : { phone: { error: true } };
    };
  }

  public static createZipValidator(): ValidatorFn {
    return (control: AbstractControl) => Validators.pattern(/^[1-9]\d{3,6}$/)(control)
      ? {
        zip: {
          error: true,
        },
      }
      : null;
  }

  public static passwordValidation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        // password should contain at least 6 digits, 1 upppercase, 1 lowercase, 1 number
        const pwdRule = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/g);
        const validPassword = pwdRule.test(control.value);
        return !validPassword ? {invalidPassword: true} : null;
      }
      return null;
    };
  }

  public static passwordMatchValidation(passwordControlName = 'password', passwordConfirmControlName = 'passwordConfirm'): ValidatorFn {
    return (group: UntypedFormGroup) => {
      const password = group.get(passwordControlName).value;
      const passwordConfirm = group.get(passwordConfirmControlName).value;
      if (!password || !passwordConfirm) {
        return null;
      }
      return password === passwordConfirm ? null : {passwordConfirmationMismatch: {valid: false}};
    };
  }

  public static forbiddenNameValidator(nameRe: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = nameRe.test(control.value);
      return forbidden ? {forbiddenName: {value: control.value}} : null;
    };
  }

  public static datePickerValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const date = DateTime.fromJSDate(control.value);
        let valid = date.isValid;
        if (valid) {
          if (date < DateTime.now().startOf('day') || date > DateTime.now().plus({years: 1})) {
            valid = false;
          }
        }
        return !valid ? {invalidDate: true} : null;
      } else {
        return null;
      }
    };
  }

  public static atLeastOneFromFormGroupRequired(): ValidatorFn {
    return (group: UntypedFormGroup) => {
      const isTrue = (element) => !!group.get(element).value;
      const valid = Object.keys(group.controls).some(isTrue);
      return valid ? null : {error: true};
    };
  }


  public static validateIpAddress(allowIPv6: boolean = true): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        let regexp;
        if (allowIPv6) {
          regexp = new RegExp(`(${ipV6Pattern})|(${ipV4Pattern})`, 'i');
          return regexp.test(control.value) ? null : {ipV6Address: true};
        } else {
          regexp = new RegExp(`${ipV4Pattern}`, 'i');
          return regexp.test(control.value) ? null : {ipAddress: true};
        }
      }
      return null;
    };
  }
}
